import type { ApiErrorCode } from '@ai-photo-style/shared';
import { localeForShop, type Locale } from './copy';

/** Foutcodes van de API plus fouten die alleen in de browser kunnen ontstaan. */
export type ErrorCode =
  | ApiErrorCode
  | 'CONFIG_NOT_FOUND'
  | 'NETWORK'
  | 'TURNSTILE_UNAVAILABLE'
  | 'KC_APPLY_FAILED'
  | 'NO_FRAME'
  | 'CATALOG_INVALID'
  | 'CATALOG_UNAVAILABLE'
  | 'CATALOG_DISABLED'
  | 'CATALOG_EMPTY'
  | 'CATALOG_LOOK_UNAVAILABLE'
  | 'CANCELED';

export class EmbedError extends Error {
  constructor(
    readonly code: ErrorCode,
    readonly retryAfterS?: number,
  ) {
    super(code);
    this.name = 'EmbedError';
  }
}

/** Uit of niet gepubliceerd: de productpagina toont geen knop en geen melding. */
export function isQuietlyUnavailable(code: ErrorCode | undefined): boolean {
  return code === 'CONFIG_NOT_FOUND' || code === 'CATALOG_DISABLED';
}

export function toEmbedError(err: unknown): EmbedError {
  if (err instanceof EmbedError) return err;
  if (err instanceof DOMException && err.name === 'AbortError') return new EmbedError('CANCELED');
  if (err instanceof TypeError) return new EmbedError('NETWORK');
  return new EmbedError('INTERNAL');
}

interface ErrorCopy {
  title: string;
  message: string;
  /** Mag dezelfde foto opnieuw geprobeerd worden? */
  retrySame: boolean;
}

function text(locale: Locale, nl: string, de: string, fr: string): string {
  if (locale === 'de') return de;
  if (locale === 'fr') return fr;
  return nl;
}

/** Klanttekst per foutcode. Kort, vriendelijk en met een uitweg. */
export function errorCopy(code: ErrorCode, retryAfterS?: number, shopId?: string): ErrorCopy {
  const locale = localeForShop(shopId);
  switch (code) {
    case 'CONFIG_NOT_FOUND':
      return { title: text(locale, 'Deze fototool is niet beschikbaar', 'Diese Fotofunktion ist nicht verfügbar', 'Cet outil photo n\'est pas disponible'), message: text(locale, 'Deze keuze bestaat niet of is nog niet gepubliceerd. Er is geen andere fototool gestart.', 'Diese Auswahl gibt es nicht oder ist noch nicht veröffentlicht. Es wurde keine andere Fotofunktion gestartet.', 'Ce choix n\'existe pas ou n\'est pas encore publié. Aucun autre outil photo n\'a été lancé.'), retrySame: false };
    case 'CATALOG_DISABLED':
      return { title: text(locale, 'Afbeeldingen maken staat uit', 'Bilder erstellen ist ausgeschaltet', 'La création d\'images est désactivée'), message: text(locale, 'Je kunt nu geen nieuwe afbeelding maken. Eerder gemaakte afbeeldingen blijven bruikbaar.', 'Du kannst jetzt kein neues Bild erstellen. Zuvor erstellte Bilder bleiben verwendbar.', 'Vous ne pouvez pas créer de nouvelle image pour le moment. Les images déjà créées restent utilisables.'), retrySame: false };
    case 'CATALOG_EMPTY':
      return { title: text(locale, 'Geen afbeeldingen beschikbaar', 'Keine Bilder verfügbar', 'Aucune image disponible'), message: text(locale, 'Er zijn op dit moment geen stijlen voor deze keuze beschikbaar.', 'Für diese Auswahl sind gerade keine Stile verfügbar.', 'Aucun style n\'est disponible pour ce choix pour le moment.'), retrySame: false };
    case 'CATALOG_LOOK_UNAVAILABLE':
    case 'UNSUPPORTED_STYLE':
      return { title: text(locale, 'Deze afbeelding is niet beschikbaar', 'Dieses Bild ist nicht verfügbar', 'Cette image n\'est pas disponible'), message: text(locale, 'De ingestelde stijl bestaat niet meer of staat uit. Er is geen andere stijl gestart.', 'Den eingestellten Stil gibt es nicht mehr oder er ist ausgeschaltet. Es wurde kein anderer Stil gestartet.', 'Le style choisi n\'existe plus ou est désactivé. Aucun autre style n\'a été lancé.'), retrySame: false };
    case 'CATALOG_CHANGED':
      return { title: text(locale, 'De stijlen zijn gewijzigd', 'Die Stile wurden geändert', 'Les styles ont changé'), message: text(locale, 'Vernieuw deze pagina om de actuele stijlen te laden. Er is geen nieuwe afbeelding gestart.', 'Lade diese Seite neu, um die aktuellen Stile zu laden. Es wurde kein neues Bild gestartet.', 'Actualisez cette page pour charger les styles actuels. Aucune nouvelle image n\'a été lancée.'), retrySame: false };
    case 'CATALOG_INVALID':
    case 'CATALOG_UNAVAILABLE':
      return { title: text(locale, 'Afbeeldingen niet beschikbaar', 'Bilder nicht verfügbar', 'Images indisponibles'), message: text(locale, 'We kunnen de stijlen voor deze winkel niet laden. Vernieuw de pagina of probeer het later opnieuw.', 'Wir können die Stile für diesen Shop nicht laden. Lade die Seite neu oder versuche es später noch einmal.', 'Nous ne pouvons pas charger les styles de cette boutique. Actualisez la page ou réessayez plus tard.'), retrySame: false };
    case 'IMAGE_TOO_SMALL':
      return {
        title: text(locale, 'Deze foto is te klein', 'Dieses Foto ist zu klein', 'Cette photo est trop petite'),
        message: text(locale, 'Kies een foto van minstens 512 pixels breed en hoog. Een foto rechtstreeks van je camera werkt het best.', 'Wähle ein Foto mit mindestens 512 Pixeln Breite und Höhe. Ein Foto direkt von deiner Kamera funktioniert am besten.', 'Choisissez une photo d\'au moins 512 × 512 pixels. Pour un meilleur résultat, utilisez une photo originale prise avec votre appareil photo.'),
        retrySame: false,
      };
    case 'IMAGE_TOO_LARGE':
      return { title: text(locale, 'Deze foto is te groot', 'Dieses Foto ist zu groß', 'Cette photo est trop grande'), message: text(locale, 'Kies een foto kleiner dan 10 MB.', 'Wähle ein Foto, das kleiner als 10 MB ist.', 'Choisissez une photo de moins de 10 Mo.'), retrySame: false };
    case 'IMAGE_UNSUPPORTED_TYPE':
    case 'IMAGE_MISSING':
      return {
        title: text(locale, 'Deze foto kunnen we niet lezen', 'Dieses Foto können wir nicht lesen', 'Nous ne parvenons pas à lire cette photo'),
        message: text(locale, 'Kies een JPG- of PNG-foto. Een foto uit je fotogalerij werkt meestal meteen.', 'Wähle ein JPG- oder PNG-Foto. Ein Foto aus deiner Galerie funktioniert meist sofort.', 'Choisissez une image au format JPG ou PNG. Une photo de votre galerie fonctionne généralement.'),
        retrySame: false,
      };
    case 'RATE_LIMITED': {
      const minutes = retryAfterS ? Math.max(1, Math.ceil(retryAfterS / 60)) : null;
      const when = locale === 'de'
        ? (minutes === null || minutes >= 60
          ? 'In einer Stunde kannst du wieder 10 Bilder erstellen.'
          : `In ${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'} kannst du wieder 10 Bilder erstellen.`)
        : locale === 'fr'
          ? (minutes === null || minutes >= 60
            ? 'Vous pourrez en créer 10 nouvelles dans une heure.'
            : `Vous pourrez en créer 10 nouvelles dans ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`)
          : (minutes === null || minutes >= 60
            ? 'Over een uur kun je er weer 10 maken.'
            : `Over ${minutes} ${minutes === 1 ? 'minuut' : 'minuten'} kun je er weer 10 maken.`);
      return {
        title: text(locale, 'De voorbeelden voor dit ontwerp zijn gebruikt', 'Die Beispiele für dieses Design sind aufgebraucht', 'Vous avez atteint la limite de créations d\'images pour ce modèle.'),
        message: text(locale, `Je kunt 10 afbeeldingen per ontwerp maken. ${when}`, `Du kannst 10 Bilder pro Design erstellen. ${when}`, `Vous pouvez générer jusqu\'à 10 images par modèle. ${when}`),
        retrySame: false,
      };
    }
    case 'GENERATION_TIMEOUT':
      return {
        title: text(locale, 'Dit duurt langer dan normaal', 'Das dauert länger als gewöhnlich', 'La création de votre image prend plus de temps que prévu'),
        message: text(locale, 'Het maken van de afbeelding lukte niet op tijd. Probeer het nog een keer.', 'Das Bild war nicht rechtzeitig fertig. Versuche es noch einmal.', 'Nous n\'avons pas pu la générer à temps. Veuillez réessayer.'),
        retrySame: true,
      };
    case 'GENERATION_FAILED':
      return {
        title: text(locale, 'Het maken van de afbeelding is niet gelukt', 'Das Bild konnte nicht erstellt werden', 'La création de l\'image a échoué'),
        message: text(locale, 'Probeer het nog een keer, of kies een andere foto.', 'Versuche es noch einmal oder wähle ein anderes Foto.', 'Veuillez réessayer ou choisissez une autre photo.'),
        retrySame: true,
      };
    case 'NETWORK':
      return {
        title: text(locale, 'Geen verbinding', 'Keine Verbindung', 'Pas de connexion'),
        message: text(locale, 'Controleer je internetverbinding en probeer het opnieuw.', 'Prüfe deine Internetverbindung und versuche es erneut.', 'Vérifiez votre connexion internet et veuillez réessayer.'),
        retrySame: true,
      };
    case 'TURNSTILE_FAILED':
    case 'TURNSTILE_UNAVAILABLE':
    case 'NONCE_MISSING':
    case 'NONCE_INVALID':
    case 'NONCE_EXPIRED':
    case 'NONCE_EXHAUSTED':
    case 'SESSION_MISMATCH':
    case 'ORIGIN_NOT_ALLOWED':
      return {
        title: text(locale, 'Even opnieuw proberen', 'Bitte noch einmal versuchen', 'Veuillez réessayer'),
        message: text(locale, 'We konden de aanvraag niet goed controleren. Probeer het nog een keer.', 'Wir konnten die Anfrage nicht richtig prüfen. Versuche es noch einmal.', 'Nous n\'avons pas pu vérifier correctement la demande.'),
        retrySame: true,
      };
    case 'KC_APPLY_FAILED':
      return {
        title: text(locale, 'Bijna gelukt', 'Fast geschafft', 'Vous y êtes presque !'),
        message: text(locale, 'Je afbeelding is klaar, maar we konden hem niet in je ontwerp zetten. Probeer het nog een keer.', 'Dein Bild ist fertig, aber wir konnten es nicht in dein Design setzen. Versuche es noch einmal.', 'Votre image est prête, mais nous n\'avons pas pu l\'intégrer à votre création. Veuillez réessayer.'),
        retrySame: true,
      };
    case 'NOT_FOUND':
      return {
        title: text(locale, 'Je aanvraag is kwijtgeraakt', 'Deine Anfrage ist verloren gegangen', 'Votre demande n\'a pas pu être traitée en raison d\'une erreur de notre côté.'),
        message: text(locale, 'Dat ligt aan ons. Probeer het nog een keer.', 'Das liegt an uns. Versuche es noch einmal.', 'Veuillez réessayer.'),
        retrySame: true,
      };
    case 'PROVIDER_UNAVAILABLE':
      return {
        title: text(locale, 'Even niet beschikbaar', 'Gerade nicht verfügbar', 'Momentanément indisponible'),
        message: text(locale, 'Het maken van afbeeldingen lukt op dit moment niet. Probeer het later nog eens.', 'Bilder erstellen klappt im Moment nicht. Versuche es später noch einmal.', 'La création d\'images ne fonctionne pas pour le moment. Veuillez réessayer plus tard.'),
        retrySame: false,
      };
    case 'NO_FRAME':
      return {
        title: text(locale, 'Geen fotovak gevonden', 'Kein Fotobereich gefunden', 'Aucun emplacement photo trouvé'),
        message: text(locale, 'Dit ontwerp heeft geen plek voor een foto. Kies een ontwerp met een foto.', 'Dieses Design hat keinen Platz für ein Foto. Wähle ein Design mit einem Foto.', 'Cette création n\'a pas de place pour une photo. Choisissez une création avec une photo.'),
        retrySame: false,
      };
    default:
      return {
        title: text(locale, 'Er ging iets mis', 'Da ist etwas schiefgelaufen', 'Une erreur s\'est produite'),
        message: text(locale, 'Probeer het nog een keer. Lukt het dan nog niet, neem dan contact op met onze klantenservice.', 'Versuche es noch einmal. Wenn es dann immer noch nicht klappt, kontaktiere unseren Kundenservice.', 'Veuillez réessayer. Si cela ne fonctionne toujours pas, contactez notre service client.'),
        retrySame: true,
      };
  }
}
